import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Building, Mail, Phone, User } from 'lucide-react';

const InstitutionalInquiry = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { seats = 25, price = 'Custom' } = location.state || {};
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    institution: '',
    department: '',
    seats: seats,
    additionalInfo: '',
    contactMethod: 'email'
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      // Encode form data for Netlify
      const form = e.target;
      const data = new FormData(form);
      
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(data).toString()
      });
      
      if (!response.ok) {
        throw new Error('Form submission failed');
      }
      
      // Form submitted successfully
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  if (submitted) {
    return (
      <div className="min-h-screen bg-black text-white pt-20 px-4">
        <div className="max-w-3xl mx-auto py-12">
          <div className="bg-gray-900 rounded-lg p-8 text-center">
            <div className="w-20 h-20 bg-blue-500/20 text-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Mail className="w-10 h-10" />
            </div>
            <h1 className="text-3xl font-bold mb-4">Thank You!</h1>
            <p className="text-xl text-gray-400 mb-8">
              We've received your inquiry about an institutional plan for ContextUI.
              Our team will contact you within 1-2 business days.
            </p>
            <button
              onClick={() => navigate('/')}
              className="bg-blue-600 text-white py-3 px-8 rounded-lg hover:bg-blue-700"
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-black text-white pt-20 px-4">
      <div className="max-w-4xl mx-auto py-12">
        <h1 className="text-3xl font-bold mb-2">Request Institutional Plan</h1>
        <p className="text-xl text-gray-400 mb-8">
          Fill out the form below and our team will get back to you with a customized solution.
        </p>
        
        {error && (
          <div className="bg-red-500/10 border border-red-500 text-red-500 p-4 rounded-lg mb-6">
            {error}
          </div>
        )}
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <form 
              onSubmit={handleSubmit}
              className="bg-gray-900 rounded-lg p-6"
              name="institutional-inquiry"
              method="POST"
              data-netlify="true"
              netlify-honeypot="bot-field"
            >
              <input type="hidden" name="form-name" value="institutional-inquiry" />
              <p className="hidden">
                <label>Don't fill this out if you're human: <input name="bot-field" /></label>
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    First Name*
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="w-full bg-gray-800 text-white rounded-lg p-3 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Last Name*
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="w-full bg-gray-800 text-white rounded-lg p-3 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Email Address*
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-gray-800 text-white rounded-lg p-3 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full bg-gray-800 text-white rounded-lg p-3 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Institution Name*
                  </label>
                  <input
                    type="text"
                    name="institution"
                    value={formData.institution}
                    onChange={handleChange}
                    required
                    className="w-full bg-gray-800 text-white rounded-lg p-3 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Department
                  </label>
                  <input
                    type="text"
                    name="department"
                    value={formData.department}
                    onChange={handleChange}
                    className="w-full bg-gray-800 text-white rounded-lg p-3 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Number of Seats*
                </label>
                <select
                  name="seats"
                  value={formData.seats}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-800 text-white rounded-lg p-3 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="10">10 seats</option>
                  <option value="25">25 seats</option>
                  <option value="50">50 seats</option>
                  <option value="100">100 seats</option>
                  <option value="custom">101+ seats (custom)</option>
                </select>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Additional Information
                </label>
                <textarea
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleChange}
                  rows="4"
                  className="w-full bg-gray-800 text-white rounded-lg p-3 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Tell us about your specific needs or requirements..."
                ></textarea>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Preferred Contact Method
                </label>
                <div className="flex space-x-4">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="contactMethod"
                      value="email"
                      checked={formData.contactMethod === 'email'}
                      onChange={handleChange}
                      className="text-blue-600"
                    />
                    <span className="ml-2">Email</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="contactMethod"
                      value="phone"
                      checked={formData.contactMethod === 'phone'}
                      onChange={handleChange}
                      className="text-blue-600"
                    />
                    <span className="ml-2">Phone</span>
                  </label>
                </div>
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg 
                         hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Submitting...' : 'Submit Inquiry'}
              </button>
            </form>
          </div>
          
          <div>
            <div className="bg-gray-900 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-semibold mb-4">Your Selected Plan</h3>
              <div className="border-b border-gray-800 pb-4 mb-4">
                <div className="text-gray-400 mb-1">Seats</div>
                <div className="text-2xl font-bold">{seats}</div>
              </div>
              <div className="border-b border-gray-800 pb-4 mb-4">
                <div className="text-gray-400 mb-1">Price per Seat</div>
                <div className="text-2xl font-bold">
                  {typeof price === 'number' ? `$${price}` : price}
                </div>
              </div>
              <div>
                <div className="text-gray-400 mb-1">Estimated Annual Total</div>
                <div className="text-2xl font-bold text-blue-500">
                  {typeof price === 'number' ? `$${price * seats * 12}` : 'Custom'}
                </div>
              </div>
            </div>
            
            <div className="bg-gray-900 rounded-lg p-6">
              <h3 className="text-xl font-semibold mb-4">Contact Us Directly</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <Mail className="w-5 h-5 text-blue-500 mr-3 mt-0.5" />
                  <div>
                    <div className="font-medium">Email</div>
                    <a href="mailto:support@isd.ai" className="text-blue-400 hover:text-blue-300">
                      esupport@isd.ai
                    </a>
                  </div>
                </div>
                <div className="flex items-start">
                  <Phone className="w-5 h-5 text-blue-500 mr-3 mt-0.5" />
                  <div>
                    <div className="font-medium">Phone</div>
                    <a href="tel:+18005551234" className="text-blue-400 hover:text-blue-300">
                      +1 (800) 555-1234
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstitutionalInquiry;